import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  requestSequence = 0

  static targets = ["status"]

  static values = {
    url: String,
    csrfToken: String,
    debounce: { type: Number, default: 250 }
  }

  connect() {
    this.schedule()
  }

  disconnect() {
    clearTimeout(this.timeout)
    this.requestSequence += 1
  }

  schedule() {
    clearTimeout(this.timeout)
    const sequence = ++this.requestSequence
    this.statusTarget.textContent = ""
    this.timeout = setTimeout(() => this.refresh(sequence), this.debounceValue)
  }

  async refresh(sequence) {
    const characters = this.activeCharacters().map((character) => this.serializeCharacter(character))

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify({ characters })
      })
      if (!response.ok) throw new Error(`Preview request failed (${response.status})`)

      const result = await response.json()
      if (sequence !== this.requestSequence) return
      if (!Array.isArray(result.codes) || result.codes.length !== characters.length) {
        throw new Error("Preview response did not match the active characters")
      }

      this.activeCharacters().forEach((character, index) => {
        const output = character.querySelector("[data-template-code-preview-target='code']")
        if (output) output.textContent = result.codes[index]
      })
      this.statusTarget.textContent = `Template codes updated for ${result.codes.length} ${result.codes.length === 1 ? "character" : "characters"}.`
    } catch (_error) {
      if (sequence !== this.requestSequence) return

      this.statusTarget.textContent = "Could not update template codes. Your edits are still here; change a value to retry."
    }
  }

  activeCharacters() {
    const characters = Array.from(this.element.querySelectorAll("[data-build-editor-character]"))
    const singleMode = this.element.querySelector("[data-build-editor-target='composition']:checked")?.value === "single"
    return singleMode ? characters.slice(0, 1) : characters
  }

  serializeCharacter(character) {
    return {
      primary_profession: Number(character.querySelector("[data-build-editor-target='primaryProfession']")?.value),
      secondary_profession: Number(character.querySelector("[data-build-editor-target='secondaryProfession']")?.value),
      skill_ids: Array.from(character.querySelectorAll("[data-build-editor-target='skill']"), (input) => Number(input.value)),
      attributes: Array.from(character.querySelectorAll("[data-build-editor-attribute]"))
        .map((row) => ({
          id: row.querySelector("[data-build-editor-target='attributeSelect']")?.value,
          points: row.querySelector("[data-build-editor-target='attributeRank']")?.value
        }))
        .filter((attribute) => attribute.id !== "")
        .map((attribute) => ({ id: Number(attribute.id), points: Number(attribute.points) }))
    }
  }
}
